import type { IntakeFormSchema, PublicPatientDetails } from '@rx-manager/shared';
import type { MessageKey } from './i18n';

/**
 * Form validation, as pure functions returning message *keys*.
 *
 * Keys rather than sentences so the same rule reads correctly in English and
 * French without the validator knowing either — and so a test can assert which
 * rule fired rather than matching prose.
 *
 * Deliberately forgiving. Every rule here also exists on the server, which is
 * what actually protects the data; this layer's only job is to catch a typo
 * before a patient loses their held slot to a round trip. Anything ambiguous is
 * allowed through and let the server decide.
 */

export type FieldErrors<K extends string = string> = Partial<Record<K, MessageKey>>;

/** Deliberately loose. Real addresses break every clever pattern. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * At least seven digits, ignoring the punctuation people actually type.
 *
 * Not E.164: a patient typing "(613) 555-0123" is giving a perfectly good
 * number, and rejecting it to satisfy a format is how a booking is abandoned.
 */
export function isUsablePhone(value: string): boolean {
  return (value.match(/\d/g) ?? []).length >= 7;
}

export function isEmail(value: string): boolean {
  return EMAIL.test(value.trim());
}

/** A real calendar date, not in the future. Returns the reason, or null. */
export function dobProblem(value: string, now: Date = new Date()): MessageKey | null {
  const trimmed = value.trim();
  if (!trimmed) return 'error.fieldRequired';
  if (!ISO_DATE.test(trimmed)) return 'error.dobInvalid';

  const [year, month, day] = trimmed.split('-').map(Number) as [
    number,
    number,
    number,
  ];
  const date = new Date(Date.UTC(year, month - 1, day));
  // Round-trip check: `Date.UTC(2026, 1, 31)` silently becomes 3 March, so an
  // impossible date has to be caught by comparing the parts back.
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return 'error.dobInvalid';
  }
  if (date.getTime() > now.getTime()) return 'error.dobFuture';
  return null;
}

export type PatientField = keyof PublicPatientDetails;

/** Every problem at once, so the patient fixes the form in one pass. */
export function validatePatient(
  patient: Partial<PublicPatientDetails>,
  now: Date = new Date(),
): FieldErrors<PatientField> {
  const errors: FieldErrors<PatientField> = {};

  if (!patient.firstName?.trim()) errors.firstName = 'error.fieldRequired';
  if (!patient.lastName?.trim()) errors.lastName = 'error.fieldRequired';

  const dob = dobProblem(patient.dob ?? '', now);
  if (dob) errors.dob = dob;

  if (!patient.email?.trim()) errors.email = 'error.fieldRequired';
  else if (!isEmail(patient.email)) errors.email = 'error.emailInvalid';

  if (!patient.phone?.trim()) errors.phone = 'error.fieldRequired';
  else if (!isUsablePhone(patient.phone)) errors.phone = 'error.phoneInvalid';

  return errors;
}

/** Required answers on the service's own intake form. */
export function validateIntake(
  schema: IntakeFormSchema | null,
  answers: Record<string, unknown>,
): FieldErrors {
  const errors: FieldErrors = {};
  for (const field of schema?.fields ?? []) {
    if (!field.required) continue;
    const value = answers[field.id];
    const empty =
      value === undefined ||
      value === null ||
      value === false ||
      (typeof value === 'string' && value.trim() === '');
    if (empty) errors[field.id] = 'error.fieldRequired';
  }
  return errors;
}

export function hasErrors(errors: FieldErrors<string>): boolean {
  return Object.keys(errors).length > 0;
}

/**
 * A schema that is safe to render.
 *
 * `formSchema` is free-form JSON a pharmacist typed, so it is checked rather
 * than trusted — a malformed one should mean "no extra questions", not a blank
 * page where the booking form used to be.
 */
export function safeFormSchema(value: unknown): IntakeFormSchema | null {
  if (!value || typeof value !== 'object') return null;
  const fields = (value as { fields?: unknown }).fields;
  if (!Array.isArray(fields)) return null;

  const valid = fields.filter(
    (field): field is IntakeFormSchema['fields'][number] =>
      Boolean(field) &&
      typeof field === 'object' &&
      typeof (field as { id?: unknown }).id === 'string' &&
      typeof (field as { label?: unknown }).label === 'string' &&
      ['text', 'textarea', 'select', 'checkbox', 'date'].includes(
        (field as { type?: unknown }).type as string,
      ),
  );
  return valid.length > 0 ? { fields: valid } : null;
}
